import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Custom } from './nestedTypes';

// ✅ Enhanced State Interface
interface CustomFilters {
  search: string;
  status: 'all' | 'draft' | 'published' | 'under_verification' | 'rejected';
  category_id?: string;
  venue_id?: string;
  sortBy: 'createdAt' | 'start_date' | 'title' | 'view_count';
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}

interface nestedState {
  // ✅ Data
  currentNested: Custom | null;
  nested: Custom[];
  categories: any[];
  venues: any[];
  
  // ✅ UI State
  filters: CustomFilters;
  selectedNestedId: string | null;
  
  // ✅ Loading States
  loading: boolean;
  error: string | null;
  
  // ✅ Pagination
  total: number;
  hasMore: boolean;
  
  // ✅ View Mode (for customPage)
  viewMode: 'grid' | 'list';
  
  // ✅ Modal States
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  CustomToDelete: string | null;
}

// ✅ Initial State
const initialState: nestedState = {
  // Data
  currentNested: null,
  nested: [],
  categories: [],
  venues: [],
  
  // Filters
  filters: {
    search: '',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 12,
  },
  
  // UI State
  selectedNestedId: null,
  viewMode: 'grid',
  
  // Loading
  loading: false,
  error: null,
  
  // Pagination
  total: 0,
  hasMore: true,
  
  // Modals
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  CustomToDelete: null,
};

// ✅ Slice
const nestedSlice = createSlice({
  name: 'nested',
  initialState,
  reducers: {
    // ✅ Custom Selection
    setCurrentNested: (state, action: PayloadAction<Custom | null>) => {
      state.currentNested = action.payload;
      state.selectedNestedId = action.payload?.documentId || null;
    },
    
    setSelectedNestedId: (state, action: PayloadAction<string | null>) => {
      state.selectedNestedId = action.payload;
    },
    
    // ✅ Filter Management
    setFilters: (state, action: PayloadAction<Partial<CustomFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      
      // Reset to page 1 when filters change (except pagination)
      if (action.payload.page === undefined) {
        state.filters.page = 1;
      }
    },
    
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.filters.page = 1; // Reset pagination
    },
    
    setStatus: (state, action: PayloadAction<CustomFilters['status']>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    
    setCategory: (state, action: PayloadAction<string | undefined>) => {
      state.filters.category_id = action.payload;
      state.filters.page = 1;
    },
    
    setVenue: (state, action: PayloadAction<string | undefined>) => {
      state.filters.venue_id = action.payload;
      state.filters.page = 1;
    },
    
    setSortBy: (state, action: PayloadAction<CustomFilters['sortBy']>) => {
      state.filters.sortBy = action.payload;
    },
    
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.filters.sortOrder = action.payload;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // ✅ View Mode
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    
    // ✅ Modal Management
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    
    openEditModal: (state, action: PayloadAction<string>) => {
      state.isEditModalOpen = true;
      state.selectedNestedId = action.payload;
    },
    
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.selectedNestedId = null;
    },
    
    openDeleteModal: (state, action: PayloadAction<string>) => {
      state.isDeleteModalOpen = true;
      state.CustomToDelete = action.payload;
    },
    
    closeDeleteModal: (state) => {
      state.isDeleteModalOpen = false;
      state.CustomToDelete = null;
    },
    
    // ✅ Error Handling
    clearcustomError: (state) => {
      state.error = null;
    },
    
    // ✅ Manual Data Updates (for optimistic updates)
    addNested: (state, action: PayloadAction<Custom>) => {
      state.nested.unshift(action.payload);
      state.total += 1;
    },
    
    updateNested: (state, action: PayloadAction<Custom>) => {
      const index = state.nested.findIndex(e => e.documentId === action.payload.documentId);
      if (index !== -1) {
        state.nested[index] = action.payload;
      }
      if (state.currentNested?.documentId === action.payload.documentId) {
        state.currentNested = action.payload;
      }
    },
    
    removeNested: (state, action: PayloadAction<string>) => {
      state.nested = state.nested.filter(e => e.documentId !== action.payload);
      state.total -= 1;
      if (state.currentNested?.documentId === action.payload) {
        state.currentNested = null;
      }
    },
  },
});

// ✅ Actions Export
export const {
  // Selection
  setCurrentNested,
  setSelectedNestedId,
  
  // Filters
  setFilters,
  setSearch,
  setStatus,
  setCategory,
  setVenue,
  setSortBy,
  setSortOrder,
  setPage,
  resetFilters,
  
  // View
  setViewMode,
  
  // Modals
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  openDeleteModal,
  closeDeleteModal,
  
  // Error
  clearcustomError,
  
  // Manual Updates
  addNested,
  updateNested,
  removeNested  ,
} = nestedSlice.actions;

// ✅ Reducer Export
export default nestedSlice.reducer;

// ✅ SELECTORS (Bonus for easy access)
export const selectNestedFilters = (state: { nested: nestedState }) => state.nested.filters;
export const selectCurrentNested = (state: { nested: nestedState }) => state.nested.currentNested;
export const selectSelectedNestedId = (state: { nested: nestedState }) => state.nested.selectedNestedId;
export const selectViewMode = (state: { nested: nestedState }) => state.nested.viewMode;
export const selectNestedLoading = (state: { nested: nestedState }) => state.nested.loading;
export const selectNestedError = (state: { nested: nestedState }) => state.nested.error;